<template>
  <main class="cashier">
    <h2>微信支付收银台</h2>
    <section v-if="order">
      <p>订单号 {{ id }}</p>
      <p>商品金额 ¥{{ money(order.total) }} · 配送费 ¥{{ money(order.deliveryFee) }}</p>
      <label class="coupons">优惠券
        <select v-model="couponId" :disabled="locked || busy">
          <option :value="null">不使用优惠券</option>
          <option v-for="coupon in coupons" :key="coupon.id" :value="coupon.id">{{ coupon.name }}（减 ¥{{ coupon.discountAmount }}）</option>
        </select>
      </label>
      <p v-if="discount > 0">优惠抵扣 −¥{{ money(discount) }}</p>
      <div class="amount">¥{{ money(total) }}</div>
      <p>{{ mode === 'JSAPI' ? '微信内安全支付' : mode === 'H5' ? '将调起微信完成支付' : '请在手机浏览器或微信内打开本页面支付' }}</p>
      <button :disabled="busy || !mode || Number(order.status) !== 0" @click="pay">{{ busy ? '处理中…' : '微信支付' }}</button>
      <button class="secondary" :disabled="busy" @click="checkResult(true)">已完成支付，查询结果</button>
      <button class="secondary" @click="$router.push('/OrderInfoView/' + id)">返回订单</button>
    </section>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <p class="hint">支付结果以微信确认结果为准。未支付或取消支付不会显示支付成功。</p>
  </main>
</template>
<script>
import request from '../axios/request'
import { paymentMode, invokeJsapi, h5PaymentUrl } from '../utils/webPayment'
export default {
  name: 'CashierView',
  data: () => ({ order: null, busy: false, error: '', ticket: '', coupons: [], couponId: null, locked: false, stopped: false }),
  computed: {
    id () { return this.$route.params.id },
    mode () { return paymentMode(navigator.userAgent) },
    gross () { return Number(this.order.total || 0) + Number(this.order.deliveryFee || 0) },
    discount () {
      if (this.locked) return Number(this.order.couponDiscount || 0)
      const coupon = this.coupons.find(c => c.id === this.couponId)
      return coupon ? Math.max(0, Math.min(Number(coupon.discountAmount), this.gross - 0.01)) : 0
    },
    total () { return this.gross - this.discount }
  },
  async mounted () {
    if (!/^\d+$/.test(this.id) || Number(this.id) > 2147483647) { this.error = '演示订单不能真实支付，请重新下单'; return }
    if (!localStorage.getItem('token')) {
      sessionStorage.setItem('webPaymentReturn', this.$route.fullPath)
      this.$router.replace('/login'); return
    }
    this.ticket = this.$route.query.payTicket || sessionStorage.getItem('payTicket:' + this.id) || ''
    if (this.$route.query.payTicket) {
      sessionStorage.setItem('payTicket:' + this.id, this.ticket)
      this.$router.replace('/cashier/' + this.id)
    }
    if (this.$route.query.authError) this.error = '微信授权失败或已过期，请重新点击微信支付'
    try {
      await this.load()
      if (this.order && (this.order.outTradeNo || this.$route.query.returned)) await this.checkResult(false)
    } catch (e) { this.error = e.message || '订单加载失败' }
    window.addEventListener('focus', this.onReturn)
    document.addEventListener('visibilitychange', this.onReturn)
  },
  beforeDestroy () { this.stopped = true; window.removeEventListener('focus', this.onReturn); document.removeEventListener('visibilitychange', this.onReturn) },
  methods: {
    money (n) { return Number(n || 0).toFixed(2) },
    async load () {
      const res = await request.get('/order/api/getOrderInfo/' + this.id)
      if (res.data.status !== 200) throw new Error(res.data.desc || '订单加载失败')
      this.order = res.data.data
      this.locked = !!this.order.outTradeNo
      if (this.locked) {
        this.couponId = this.order.couponId || null
        if (this.couponId) this.coupons = [{ id: this.couponId, name: '本订单已选优惠券', discountAmount: this.order.couponDiscount }]
      } else {
        const scope = Number(this.order.serviceMode) === 1 ? 'in_store' : Number(this.order.orderType) >= 9 ? 'home_service' : 'takeout'
        const result = await request.get('/coupon/api/available', { params: { scope, amount: this.money(this.gross) } })
        if (result.data.status === 200) this.coupons = result.data.data || []
        const saved = sessionStorage.getItem('payCoupon:' + this.id)
        if (saved && this.coupons.some(c => String(c.id) === saved)) this.couponId = Number(saved)
      }
    },
    onReturn () { if (!document.hidden && this.order && this.order.outTradeNo && !this.busy) this.checkResult(false) },
    async checkResult (showPending) {
      if (this.busy || this.stopped) return false
      this.busy = true
      try { return await this.queryPaid(showPending) }
      catch (e) { this.error = '暂时无法确认支付结果，请稍后查询，不要重复下单'; return false }
      finally { this.busy = false }
    },
    async queryPaid (showPending) {
      const res = await request.post('/api/pay/web/' + this.id + '/status')
      if (res.data.status !== 200) throw new Error(res.data.desc || '查询失败')
      if (res.data.data.paid) {
        if (!this.stopped) this.$router.replace('/paySuccess/' + this.id)
        return true
      }
      if (showPending) this.error = '尚未确认支付成功。如已扣款，请稍后再次查询。'
      return false
    },
    async pay () {
      if (this.busy || !this.mode) return
      this.error = ''; this.busy = true
      try {
        if (this.order.outTradeNo && await this.queryPaid(false)) return
        if (this.mode === 'JSAPI' && !this.ticket) {
          sessionStorage.setItem('payCoupon:' + this.id, this.couponId == null ? '' : String(this.couponId))
          const auth = await request.post('/api/pay/web/' + this.id + '/authorize')
          if (auth.data.status !== 200) throw new Error(auth.data.desc || '微信授权失败')
          window.location.assign(auth.data.data.url); return
        }
        const res = await request.post('/api/pay/web/' + this.id + '/prepay', { mode: this.mode, ticket: this.ticket, couponId: this.couponId })
        if (res.data.status !== 200) {
          if (res.data.data && res.data.data.reauthorize) { this.ticket = ''; sessionStorage.removeItem('payTicket:' + this.id) }
          throw new Error(res.data.desc || '发起支付失败')
        }
        await this.load()
        if (this.mode === 'H5') {
          window.location.assign(h5PaymentUrl(res.data.data.h5Url, window.location.origin + '/#/cashier/' + this.id + '?returned=1')); return
        }
        await invokeJsapi(res.data.data)
        for (let i = 0; i < 5 && !this.stopped; i++) {
          if (await this.queryPaid(false)) return
          await new Promise(resolve => setTimeout(resolve, 1500))
        }
        this.error = '微信正在确认支付结果，请稍后点击查询结果'
      } catch (e) { this.error = e.message || '支付未完成，请稍后重试' }
      finally { this.busy = false }
    }
  }
}
</script>
<style scoped>
.cashier{box-sizing:border-box;max-width:540px;margin:0 auto;padding:24px 18px 100px;color:#222;text-align:center}
section{background:#fff;padding:24px 18px;border-radius:16px}.amount{font-size:36px;font-weight:700;margin:24px 0;color:#ed9518}
button{box-sizing:border-box;width:100%;padding:14px;margin-top:12px;border:0;border-radius:10px;background:#ffdb25;color:#111;font-size:16px;font-weight:600;cursor:pointer}
button:disabled{opacity:.5;cursor:default}.secondary{background:#f2f3f5}.error{color:#c52d2d}.hint{font-size:13px;color:#777;line-height:1.6}.coupons{display:block;background:#fff4bd;padding:14px;border-radius:10px}select{max-width:100%;margin-top:8px;padding:8px}
</style>
